import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Warning, Lock, Scales, Globe, EnvelopeSimple } from "@phosphor-icons/react";
import { LCShieldLogo } from "@/components/LCShieldLogo";
import { Seo } from "@/components/Seo";
import { FooterLinks } from "@/components/FooterLinks";
import { useI18n } from "@/i18n";
import { getLegal } from "@/i18n/legal";

function LegalShell({ title, updated, icon: Icon, children }) {
  const nav = useNavigate();
  const { lang } = useI18n();
  const c = getLegal(lang).common;
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-screen bg-lc-navy text-white font-sans antialiased flex flex-col">
      <nav className="sticky top-0 z-40 border-b border-white/10 bg-lc-navy/85 backdrop-blur-md">
        <div className="mx-auto max-w-4xl px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="legal-logo">
            <LCShieldLogo className="text-lc-sky" size={24} />
            <span className="font-serif text-xl tracking-tight">LC Shield</span>
          </Link>
          <button onClick={() => nav("/workspace")} data-testid="legal-open-workspace"
            className="inline-flex items-center gap-2 rounded-sm bg-lc-sky px-4 py-2 text-sm font-semibold text-lc-navy hover:bg-white transition-colors duration-200">
            {c.openWorkspace}
          </button>
        </div>
      </nav>

      <header className="border-b border-white/10">
        <div className="mx-auto max-w-4xl px-6 py-14">
          <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-lc-fog hover:text-white transition-colors duration-200">
            <ArrowLeft size={13} weight="bold" /> {c.backHome}
          </Link>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="mt-6 flex items-center gap-3">
              <Icon size={30} weight="duotone" className="text-lc-sky shrink-0" />
              <h1 className="font-serif font-semibold tracking-tight text-4xl sm:text-5xl">{title}</h1>
            </div>
            {updated && <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-lc-fog">{updated}</p>}
          </motion.div>
        </div>
      </header>

      <main className="flex-1">
        <motion.article
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
          className="mx-auto max-w-4xl px-6 py-14 space-y-10">
          {children}
        </motion.article>
      </main>

      <footer className="border-t border-white/10">
        <div className="mx-auto max-w-4xl px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <LCShieldLogo className="text-lc-sky" size={20} />
            <span className="font-serif text-lg text-white">LC Shield</span>
          </div>
          <FooterLinks variant="dark" />
        </div>
      </footer>
    </div>
  );
}

const H2 = ({ n, children }) => (
  <h2 className="font-serif text-2xl text-white">
    {n != null && <span className="mr-2 font-mono text-base text-lc-sky">{n}</span>}{children}
  </h2>
);
const P = ({ children }) => <p className="text-sm leading-relaxed text-lc-fog">{children}</p>;
const Item = ({ b, text, link }) => (
  <li className="flex gap-2 text-sm leading-relaxed text-lc-fog">
    <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-lc-sky" />
    <span>{b && <strong className="text-white">{b} </strong>}{text}{link && <> <Link to={link.href} className="text-lc-sky hover:underline">{link.label} →</Link></>}</span>
  </li>
);
const Callout = ({ tone = "warn", title, items }) => (
  <div className={`rounded-sm border p-5 ${tone === "warn" ? "border-lc-warn/30 bg-lc-warn/5" : "border-lc-ok/30 bg-lc-ok/5"}`}>
    {title && (
      <p className={`flex items-center gap-2 text-sm font-bold ${tone === "warn" ? "text-lc-warn" : "text-lc-ok"}`}>
        {tone === "warn" ? <Warning size={16} weight="fill" /> : <Lock size={16} weight="fill" />}{title}
      </p>
    )}
    <ul className="mt-2 space-y-2">{items.map((it, i) => <Item key={i} {...it} />)}</ul>
  </div>
);

const Section = ({ s }) => (
  <section className="space-y-4">
    <H2 n={s.n}>{s.h}</H2>
    {s.p && <P>{s.p}</P>}
    {s.callout && <Callout {...s.callout} />}
    {s.items && <ul className="space-y-2">{s.items.map((it, i) => <Item key={i} {...it} />)}</ul>}
    {s.ps && s.ps.map((p, i) => <P key={i}>{p}</P>)}
  </section>
);

export function Terms() {
  const { lang } = useI18n();
  const d = getLegal(lang).terms;
  return (
    <LegalShell title={d.title} updated={d.updated} icon={Scales}>
      <Seo path="/terms"
        title="Terms of Service | LC Shield — Documentary Credit Assistant"
        description="Read the LC Shield Terms of Service: scope of our non-binding UCP 600 / ISBP 821 (2023) document-check assistant, liability limits, BYO-Key usage, billing and HKIAC-governed dispute resolution." />
      <P>{d.intro}</P>
      {d.sections.map((s, i) => <Section key={i} s={s} />)}
    </LegalShell>
  );
}

export function Privacy() {
  const { lang } = useI18n();
  const d = getLegal(lang).privacy;
  return (
    <LegalShell title={d.title} updated={d.updated} icon={Lock}>
      <Seo path="/privacy"
        title="Privacy Policy | LC Shield — Documents Not Retained After Analysis"
        description="How LC Shield, a product of DZent Limited (Hong Kong), handles the letter-of-credit documents you upload for pre-checks." />
      <P>{d.intro}</P>
      {d.sections.map((s, i) => <Section key={i} s={s} />)}
    </LegalShell>
  );
}

export function About() {
  const { lang } = useI18n();
  const d = getLegal(lang).about;
  return (
    <LegalShell title={d.title} updated={d.updated} icon={Globe}>
      <Seo path="/about"
        title="About LC Shield | AI Documentary Credit & UCP 600 Experts"
        description="LC Shield pre-checks letter-of-credit documents against UCP 600 to help exporters and their teams catch likely discrepancies before bank presentation. A product of DZent Limited, Hong Kong." />
      <P>{d.p1}</P>
      <P>{d.p2}</P>

      <div className="grid sm:grid-cols-3 gap-4">
        {d.stats.map((s) => (
          <div key={s.k} className="rounded-sm border border-white/10 bg-lc-slate/30 p-5">
            <div className="font-serif text-3xl text-lc-sky">{s.k}</div>
            <p className="mt-2 text-xs leading-relaxed text-lc-fog">{s.v}</p>
          </div>
        ))}
      </div>

      <P>{d.p3}</P>
      <P>{d.p4}</P>
      <P>{d.p5}</P>
      <P>{d.p6}</P>
      <P>{d.p7}</P>

      <div className="grid sm:grid-cols-3 gap-4 border-t border-white/10 pt-8">
        {d.hqLabel && (
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-lc-sky">{d.hqLabel}</p>
            <p className="mt-1 text-sm text-white">{d.hqValue}</p>
          </div>
        )}
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-lc-sky">{d.reachLabel}</p>
          <p className="mt-1 text-sm text-white">{d.reachValue}</p>
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-lc-sky">{d.inquiriesLabel}</p>
          <a href="mailto:support@getlcshield.com" className="mt-1 inline-flex items-center gap-1.5 text-sm text-white hover:text-lc-sky transition-colors duration-200">
            <EnvelopeSimple size={15} weight="duotone" /> support@getlcshield.com
          </a>
        </div>
      </div>
    </LegalShell>
  );
}
